import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Car, Gauge, Palette, Tag, MessageCircle, CarFront,
    Heart, Check, Columns2, BadgeDollarSign,
    Landmark, Repeat, Bookmark, FileText, ListChecks, Images, BadgePercent, Info,
} from 'lucide-react';
import { Share2, Volume2, VolumeX } from 'lucide-react';
import { Button } from '../ui/button';
import { wallpaperBgStyle, hasWallpaper, cardSurfaceClass } from '../shared/vehiclePresentation';
import { resolveBuyerTheme, TAP_BLUE, TAP_BLUE_HOVER, TAP_ORANGE, TAP_ORANGE_HOVER, TAP_FUCHSIA, TAP_FUCHSIA_HOVER } from './buyerTheme';
import { feedback, tactileEnabled, setTactileEnabled, tactileAllowedByOrg } from './tactile';
import { ACTION_GRID, ActionChip, ActionTile } from './ActionChips';
import { VehicleSpecsPanel } from './VehicleSpecsPanel';
import { APR, DEFAULT_TERM_MONTHS, money, monthlyEstimate, priceText, mileageText } from '../../lib/financeMath';

/**
 * VehicleShowroom — the PREMIUM customer-facing presentation of a single
 * vehicle. Purely presentational: the public page and the staff Preview both
 * render this same component, so staff always preview exactly what buyers see.
 */
export const VehicleShowroom = ({
    vehicle: v,
    dealership,
    dealerLogo = null,
    salesStatus,
    topRight = null,
    topLeft = null,
    preview = false,
    inCompare = false,
    onContact, onTestDrive, onSave, onCompare, onShare, onOfferDeposit,
    onFinance, onTradeIn, onDetails, onImages, onPromotion,
    promotionOn = false, promotionUrl = null, theme = 'dark',
    paymentEstimate = false,
    // Fired when the buyer opens the CARFAX report, so Buyer Activity can show
    // "Checked CARFAX". Optional: the Sales Desk renders this same component to
    // show a salesperson what the buyer sees, and a staff view must not log a
    // buyer action.
    onCarfaxView = null,
}) => {
    const { t } = useTranslation();
    const th = resolveBuyerTheme(theme);
    const isLight = th.name === 'light';
    const [sendOpen, setSendOpen] = useState(false);
    const [idx, setIdx] = useState(0);
    const [estInfoOpen, setEstInfoOpen] = useState(false);
    // Same shared payment() + APR the F&I screen uses, on the SAME defaults it
    // lands on (60 months, 10% down) — only the frequency is pinned to monthly.
    // null when asking_price holds no usable number ("Call for price"), which
    // silently falls back to the price-only row.
    const estPerMonth = paymentEstimate && onFinance ? monthlyEstimate(v?.asking_price) : null;
    const touchX = useRef(null);
    const rootRef = useRef(null);
    const [soundOn, setSoundOn] = useState(tactileEnabled);
    const toggleSound = () => {
        const next = !soundOn;
        setTactileEnabled(next);
        setSoundOn(next);
        if (next) feedback('select');            // confirm it audibly, once
    };

    const photos = v?.photos || [];
    const title = `${v?.year || ''} ${v?.make || ''} ${v?.model || ''}`.trim();
    const carfaxLive = !!(v?.carfax_enabled && v?.carfax_url) && !preview;
    const wall = hasWallpaper(v);
    // In the automotive demo, the bottom-center AutoDemoSwitcher pill overlays
    // the bottom of the viewport; reserve dock space so nothing sits under it.
    const autoDemo = typeof window !== 'undefined' && sessionStorage.getItem('yulontap_auto_demo') === '1';
    const go = (d) => { if (photos.length) setIdx((i) => (i + d + photos.length) % photos.length); };
    const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
    const onTouchEnd = (e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
        touchX.current = null;
    };

    return (
        <div ref={rootRef} className={th.page} data-testid="vehicle-showroom" data-buyer-theme={th.name}>
            {/* Sticky glass header */}
            <div className={`${preview ? 'relative z-0' : 'sticky top-0 z-30'} ${th.header}`}>
                <div className="max-w-md mx-auto flex items-center justify-between px-4 h-12">
                    <div className="flex items-center gap-2 min-w-0">
                        {topLeft}
                        {dealerLogo ? (
                            <img src={dealerLogo} alt={dealership} className="h-7 w-auto max-w-[88px] object-contain shrink-0" data-testid="vs-dealer-logo" />
                        ) : null}
                        <span className={`text-[11px] font-semibold tracking-[0.18em] uppercase truncate ${th.dealership}`} data-testid="vs-dealership">{dealership}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        {!preview && tactileAllowedByOrg() && (
                            <button type="button" onClick={toggleSound} data-tactile="select"
                                aria-label={soundOn ? t('tactile.mute', 'Turn off sounds') : t('tactile.unmute', 'Turn on sounds')}
                                aria-pressed={soundOn}
                                title={soundOn ? t('tactile.mute', 'Turn off sounds') : t('tactile.unmute', 'Turn on sounds')}
                                data-testid="vs-sound-toggle"
                                className={`p-2 rounded-full transition-colors ${th.muted} hover:opacity-80`}>
                                {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                            </button>
                        )}
                        {topRight}
                    </div>
                </div>
            </div>

            {/* Bottom padding clears the compact dock only (64px + safe area);
                the auto-demo switcher pill needs extra room when present. */}
            {/* pb clears the fixed action dock — with four secondary buttons the
                last one otherwise sits under it and cannot be tapped. */}
            <div className={`max-w-md mx-auto ${autoDemo && !preview ? 'pb-44' : 'pb-32'}`}>
                {/* Cinematic hero gallery */}
                <div
                    className={`relative aspect-[4/3] overflow-hidden flex items-center justify-center select-none ${th.gallery}`}
                    style={wallpaperBgStyle(v) || undefined}
                    onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
                    onClick={onImages}
                    data-testid="vs-gallery"
                >
                    {photos.length ? (
                        wall ? (
                            <div className={`m-6 rounded-2xl overflow-hidden ${cardSurfaceClass(v.card_style, 'dark')} p-2`}>
                                <img src={photos[idx]} alt={title} className="max-h-[52vh] w-auto object-contain rounded-xl" data-testid="vs-photo" />
                            </div>
                        ) : (
                            <img src={photos[idx]} alt={title} className="w-full h-full object-cover transition-opacity duration-300" data-testid="vs-photo" />
                        )
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-neutral-700" data-testid="vs-no-photo"><Car className="w-20 h-20" /></div>
                    )}

                    {promotionOn ? (
                        <a href={promotionUrl || undefined} target={promotionUrl ? '_blank' : undefined} rel="noopener noreferrer"
                            onClick={(e) => { e.stopPropagation(); if (!promotionUrl) { e.preventDefault(); onPromotion?.(); } }}
                            data-testid="vs-promotion-badge" data-tactile="select"
                            /* Centred over the hero, near the top. The PILL never
                               changes opacity or scale — only the green ring behind
                               it expands and fades, so there is no brightness spike
                               against the photo (that spike is what made the old
                               Review Shield orb read as aggressive). */
                            className="absolute top-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-[15px] font-bold text-neutral-950 ring-1 ring-emerald-200/70 animate-promo-pulse motion-reduce:animate-none">
                            <BadgePercent className="w-[18px] h-[18px]" strokeWidth={2.25} />
                            {t('vehicleExp.promotion', 'Promotion')}
                        </a>
                    ) : null}

                    {/* bottom scrim + title/price overlay */}
                    <div className={`absolute inset-x-0 bottom-0 h-2/3 pointer-events-none ${th.scrim}`} />
                    <div className="absolute inset-x-0 bottom-0 p-4">
                        <h1 className={`text-3xl font-extrabold leading-[1.05] drop-shadow ${th.title}`} data-testid="vs-title">{title}</h1>
                        {v?.trim ? <p className={`font-semibold mt-1 text-sm tracking-wide ${th.accent}`} data-testid="vs-trim">{v.trim}</p> : null}
                        {v?.asking_price ? (
                            /* Price + monthly estimate share one row at the SAME
                               size and weight, tops aligned with leading-none so
                               their baselines land level, split by a hairline
                               rule. "Get pre-qualified" hangs under the monthly
                               figure only — it is a second door into the same
                               F&I flow the Finance tile opens, not a row-wide
                               action. When the estimate is off (or the price is
                               unparseable) this collapses back to the bare
                               price paragraph it has always been. */
                            <div className="mt-1.5 flex items-start gap-3" data-testid="vs-price-row">
                                <p className={`text-2xl font-black leading-none ${th.price}`} data-testid="vs-price">{priceText(v.asking_price)}</p>
                                {estPerMonth ? (
                                    <>
                                        <span aria-hidden="true" data-testid="vs-price-divider"
                                            className={`w-px h-6 shrink-0 ${isLight ? 'bg-slate-300' : 'bg-white/25'}`} />
                                        <div className="flex flex-col items-start min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <p className={`text-2xl font-black leading-none ${th.title}`} data-testid="vs-payment-estimate">
                                                    {money(estPerMonth)}{t('vehicleExp.payment.perMonth', '/mo')}
                                                </p>
                                                <button type="button" data-testid="vs-payment-info"
                                                    aria-label={t('vehicleExp.payment.infoLabel', 'About this estimate')}
                                                    aria-expanded={estInfoOpen}
                                                    onClick={(e) => { e.stopPropagation(); setEstInfoOpen((o) => !o); }}
                                                    className={`shrink-0 rounded-full transition-opacity hover:opacity-100 ${isLight ? 'text-slate-500' : 'text-neutral-400'} opacity-80`}>
                                                    <Info className="w-[15px] h-[15px]" strokeWidth={2.25} />
                                                </button>
                                            </div>
                                            <button type="button" data-testid="vs-payment-prequalify" data-tactile="select"
                                                onClick={(e) => { e.stopPropagation(); onFinance(); }}
                                                className="mt-1 text-[13px] font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity"
                                                style={{ color: isLight ? TAP_BLUE : '#5B93FF' }}>
                                                {t('vehicleExp.payment.prequalify', 'Get pre-qualified')} →
                                            </button>
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        ) : null}
                        {estPerMonth && estInfoOpen ? (
                            /* The EXISTING F&I disclaimer string, verbatim. */
                            <p onClick={(e) => e.stopPropagation()} data-testid="vs-payment-disclaimer"
                                className={`mt-2 text-[11px] leading-relaxed rounded-lg px-2.5 py-2 ${isLight ? 'bg-white/90 text-slate-600' : 'bg-black/60 text-neutral-300'}`}>
                                {t('fi.financing.disclaimer', 'An estimate for planning only, at an example rate of {{apr}}% — not an offer of credit or an approval.', { apr: (APR * 100).toFixed(1) })}
                                {' '}{DEFAULT_TERM_MONTHS} {t('fi.financing.months', 'months')}.
                            </p>
                        ) : null}
                    </div>

                    {photos.length > 1 && (
                        <button type="button" onClick={onImages} data-testid="vs-hero-more-photos" data-tactile="select"
                            aria-label={t('vehicleExp.morePhotos', 'More photos')}
                            className="absolute bottom-3 right-3 text-[11px] font-semibold bg-black/55 hover:bg-black/75 rounded-full px-2.5 py-1 flex items-center gap-1.5 transition-colors">
                            <Images className="w-3.5 h-3.5" />{photos.length}
                        </button>
                    )}
                </div>

                {/* Quick spec chips */}
                <div className="px-4 mt-4 flex flex-wrap gap-2" data-testid="vs-chips">
                    {v?.mileage ? <span className={`inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 ${th.chip}`} data-testid="vs-mileage"><Gauge className={`w-4 h-4 ${th.chipIcon}`} />{mileageText(v.mileage)}</span> : null}
                    {v?.colour ? <span className={`inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 ${th.chip}`} data-testid="vs-colour"><Palette className={`w-4 h-4 ${th.chipIcon}`} />{v.colour}</span> : null}
                    {v?.stock_number ? <span className={`inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 ${th.chip}`} data-testid="vs-stock"><Tag className={`w-4 h-4 ${th.chipIcon}`} />{t('usedVehicles.stockShort', 'Stock')} #{v.stock_number}</span> : null}
                </div>

                {/* Secondary actions — in the normal scroll flow, right under the
                    hero. Only Test Drive + Contact Sales stay pinned in the dock,
                    so the photo, title and price own the first screen.
                    Buyer-only: staff preview keeps its own chrome. */}
                {/* Icon-chip cards: colour lives in the chip ring, so the dock's
                    two filled pills keep the strongest weight. */}
                {!preview ? (
                    <div className="px-4 mt-4 space-y-2.5" data-testid="vs-secondary-actions">
                        <div className={ACTION_GRID}>
                            {onOfferDeposit && (
                                <ActionTile light={isLight} color="#34D399" icon={BadgeDollarSign}
                                    label={t('offerDeposit.dockButton', 'Make an Offer / Leave a Deposit')}
                                    onClick={onOfferDeposit} testid="vs-offer-deposit" />
                            )}
                            {/* CARFAX keeps its own gold, and the SAME two-element
                                structure as every other tile (ring + label) so the
                                icon sits on the same baseline as its neighbours. The
                                wordmark badge that used to sit between them is what
                                pushed this tile out of alignment. */}
                            {(carfaxLive || autoDemo) && (
                                <ActionTile light={isLight} color="#E3B23C" icon={FileText}
                                    testid="vs-carfax"
                                    label={
                                        /* Brand FIRST, then the verb underneath: a
                                           multi-market trilingual buyer may not know
                                           the CARFAX mark, so the tile has to say what
                                           tapping it does. Dark artwork keeps its white
                                           backing on the #161616 card. */
                                        <span className="inline-flex items-center rounded bg-white px-2 py-[4px]">
                                            <img src="/carfax-wordmark.jpg" alt="CARFAX" className="h-4 w-auto" data-testid="vs-carfax-logo" />
                                        </span>
                                    }
                                    sublabel={
                                        <span className={`text-[13px] font-semibold leading-tight ${isLight ? 'text-neutral-900' : 'text-neutral-100'}`}
                                            data-testid="vs-carfax-sublabel">
                                            {t('offerDeposit.carfaxReport', 'History Report')}
                                        </span>
                                    }
                                    onClick={() => { if (carfaxLive) { onCarfaxView?.(); window.open(v.carfax_url, '_blank', 'noopener,noreferrer'); } }} />
                            )}
                            {onFinance && (
                                <ActionTile light={isLight} color={TAP_BLUE} icon={Landmark}
                                    label={t('fi.button', 'Finance & Insurance')}
                                    onClick={onFinance} testid="vs-finance" />
                            )}
                            {onTradeIn && (
                                <ActionTile light={isLight} color={TAP_FUCHSIA} icon={Repeat}
                                    label={t('tradeIn.button', 'Trade In')}
                                    onClick={onTradeIn} testid="vs-tradein" />
                            )}
                            {/* All six get the full tile treatment — 2 columns x 3
                                rows, no odd cell left over. */}
                            <ActionTile light={isLight} color="#38BDF8" icon={ListChecks}
                                label={t('vehicleExp.detailsSpecsTitle', 'Vehicle details & specs')}
                                onClick={onDetails} testid="vs-details-specs" />
                            <ActionTile light={isLight} color={TAP_ORANGE} icon={Bookmark}
                                label={t('vehicleExp.saveOrSendVehicle', 'Save or send this vehicle')}
                                onClick={() => setSendOpen(true)} testid="vs-save-send" />
                        </div>
                    </div>
                ) : null}

                {/* Staff preview keeps the specs/features INLINE — the salesperson
                    is checking fidelity inside a modal, so sending them to another
                    route would lose the preview. Same renderer either way. */}
                {preview ? <VehicleSpecsPanel vehicle={v} th={th} t={t} /> : null}

                {/* Sales-hours (open/closed) logic remains internal — intentionally NOT rendered
                    as a customer-facing status card in the public showroom action area. */}

                <p className={`text-center text-[11px] mt-8 ${th.faint}`}>{t('vehicleExp.poweredBy', 'Powered by YulOnTap')}</p>
            </div>

            {/* Save / Send / Compare sheet — three large, obvious choices. */}
            {sendOpen && !preview && (
                <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="vs-save-send-sheet">
                    <button className="absolute inset-0 bg-black/60" aria-label={t('common.close', 'Close')}
                        onClick={() => setSendOpen(false)} data-testid="vs-save-send-backdrop" />
                    <div className={`relative w-full max-w-md rounded-t-3xl p-4 pb-8 space-y-2.5 ${th.sheet}`}
                        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)' }}>
                        <p className={`text-center text-sm font-semibold pb-1 ${th.sectionTitle}`}>{t('vehicleExp.saveOrSend', 'Save / Send')}</p>
                        <button onClick={() => { setSendOpen(false); onSave && onSave(); }}
                            className={`w-full flex items-center gap-3 rounded-2xl border px-4 py-4 text-left transition-colors ${th.pickBtn}`}
                            data-testid="vs-sheet-save">
                            <ActionChip color={TAP_FUCHSIA}><Heart className="w-[17px] h-[17px]" strokeWidth={1.75} /></ActionChip>
                            <span className="font-semibold">{t('vehicleExp.saveShort', 'Save')}</span>
                        </button>
                        {onShare && (
                            <button onClick={() => { setSendOpen(false); onShare(); }}
                                className={`w-full flex items-center gap-3 rounded-2xl border px-4 py-4 text-left transition-colors ${th.pickBtn}`}
                                data-testid="vs-sheet-send">
                                <ActionChip color="#38BDF8"><Share2 className="w-[17px] h-[17px]" strokeWidth={1.75} /></ActionChip>
                                <span className="font-semibold">{t('vehicleShare.sendToSomeone', 'Send to someone')}</span>
                            </button>
                        )}
                        {onCompare && (
                            <button onClick={() => { setSendOpen(false); onCompare(); }}
                                className={`w-full flex items-center gap-3 rounded-2xl border px-4 py-4 text-left transition-colors ${th.pickBtn}`}
                                data-testid="vs-sheet-compare">
                                <ActionChip color={TAP_ORANGE}>{inCompare ? <Check className="w-[17px] h-[17px]" strokeWidth={1.75} /> : <Columns2 className="w-[17px] h-[17px]" strokeWidth={1.75} />}</ActionChip>
                                <span className="font-semibold">{inCompare ? t('vehicleExp.added', 'Added') : t('vehicleExp.addToComparison', 'Add to comparison')}</span>
                            </button>
                        )}
                        <button onClick={() => setSendOpen(false)}
                            className={`w-full rounded-2xl px-4 py-3.5 text-sm font-semibold ${th.muted}`}
                            data-testid="vs-sheet-cancel">{t('common.cancel', 'Cancel')}</button>
                    </div>
                </div>
            )}

            {/* Compact sticky dock — buyer-only, ONE row (~64px) with just the two
                conversion actions. Hidden in preview so the salesperson's own chrome
                (reply bar / Ask Manager) owns the edges. */}
            {!preview && (
            <div className={`fixed bottom-0 inset-x-0 z-30 ${th.dock}`} data-testid="vs-action-dock" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                <div className={`max-w-md mx-auto px-3 py-2 ${autoDemo ? 'pb-20' : ''}`}>
                    <div className="grid grid-cols-2 gap-2.5">
                        <Button
                            className="w-full h-12 text-[14px] px-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-bold shadow-lg shadow-emerald-900/30 transition-transform active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100"
                            onClick={onTestDrive}
                            data-testid="vs-testdrive"
                            data-tactile="select"
                        >
                            <CarFront className="w-5 h-5 mr-2" />{t('vehicleExp.testDriveShort', 'Test Drive')}
                        </Button>
                        <Button
                            className="w-full h-12 text-[14px] px-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold shadow-lg shadow-blue-900/30 transition-transform active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100"
                            onClick={onContact}
                            data-testid="vs-contact"
                            data-tactile="select"
                        >
                            <MessageCircle className="w-5 h-5 mr-2" />{t('vehicleExp.contactSales', 'Contact Sales')}
                        </Button>
                    </div>
                </div>
            </div>
            )}
        </div>
    );
};

export default VehicleShowroom;
